import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Helmet } from 'react-helmet-async';

const ComplaintsDisputes = () => {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#F0FDF4' }}>
            <Helmet>
                <title>Complaints &amp; Disputes Procedure | Sampler</title>
                <meta name="description" content="Sampler Complaints and Disputes Procedure — Ritson Portfolio Ltd." />
            </Helmet>

            {/* Sticky header */}
            <div className="sticky top-0 z-10 border-b border-purple-100 bg-white/80 backdrop-blur-md">
                <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
                    <Button variant="ghost" onClick={() => navigate(-1)} className="text-slate-600 hover:text-[#8B5CF6] -ml-2" data-testid="legal-back-btn">
                        <ArrowLeft className="mr-2 h-4 w-4" /> Back
                    </Button>
                    <img src="/sampler-logo.png" alt="Sampler" className="h-8 w-auto" />
                </div>
            </div>

            {/* Document body */}
            <div className="max-w-3xl mx-auto px-4 py-12">
                <div className="bg-white rounded-3xl border border-purple-200/50 shadow-sm p-8 md:p-12">

                    {/* Document header */}
                    <div className="mb-10 pb-8 border-b border-purple-100">
                        <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] block mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                            Legal
                        </span>
                        <h1 className="text-3xl font-bold mb-5 tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>
                            Complaints and Disputes Procedure
                        </h1>
                        <div className="text-sm text-slate-500 space-y-1" style={{ fontFamily: 'Barlow, sans-serif' }}>
                            <p>Ritson Portfolio Ltd (trading as Sampler) | Company Number: 17242741 | Registered in England and Wales</p>
                            <p>Registered Office: Ritson Haven, Ritson Road, London, E8 1DE | ICO Registration: ZC195328</p>
                            <p className="font-semibold text-slate-700 mt-2">Internal and external procedure | Version 1.1 | Effective Date: 13 July 2026 | Owner: Jack (Co-Founder)</p>
                        </div>
                    </div>

                    <div className="space-y-10 text-slate-700" style={{ fontFamily: 'Barlow, sans-serif' }}>

                        {/* 1 */}
                        <Section title="1. Purpose and Scope">
                            <Clause>This procedure sets out how Sampler handles complaints and disputes raised by candidates, employer clients, or any other party in connection with the Sampler platform. It applies to:</Clause>
                            <BulletList items={[
                                'Complaints about the platform, its features, or the service received',
                                'Disputes about payments, refunds, or invoices',
                                'Concerns about candidate conduct during a trial shift',
                                'Concerns about employer conduct during a trial shift',
                                'Discrimination or unfair treatment concerns',
                                'Data protection complaints',
                                'Disputes about Talent Card outputs or assessment results',
                            ]} />
                            <Clause>This procedure does not cover employment disputes between candidates and employers, for which Sampler is not a party. Candidates with employment law concerns should seek independent legal advice or contact ACAS.</Clause>
                        </Section>

                        {/* 2 */}
                        <Section title="2. Who to Contact">
                            <Clause>All complaints and disputes should be submitted in writing to:</Clause>
                            <div className="mt-2 ml-4 text-sm">
                                <p>Email: <a href="mailto:complaints@sampler.org.uk" className="text-[#8B5CF6] hover:underline">complaints@sampler.org.uk</a></p>
                            </div>
                            <Clause>Subject line: Please include one of the following — "Platform complaint", "Payment dispute", "Trial shift concern", or "Data protection complaint".</Clause>
                            <Clause>Written submissions ensure a clear record of the complaint and help us respond accurately and promptly. We do not handle complaints by phone at this stage, but will arrange a call if appropriate once a complaint has been logged in writing.</Clause>
                        </Section>

                        {/* 3 */}
                        <Section title="3. Complaints Process">
                            <SubHeading>3.1 Candidate Complaints</SubHeading>
                            <Clause>The following process applies to complaints raised by candidates:</Clause>
                            <Table
                                headers={['Step', 'Action']}
                                rows={[
                                    ['1. Submit', 'Candidate emails complaints@sampler.org.uk with a clear description of the issue, the date it occurred, and any relevant booking reference or employer name.'],
                                    ['2. Acknowledge', 'Sampler acknowledges receipt within 2 working days. The complaint is logged with a reference number, date, and category.'],
                                    ['3. Investigate', 'Sampler investigates the complaint, which may include reviewing platform records, contacting the employer client, and reviewing communications. The candidate may be asked for additional information.'],
                                    ['4. Respond', 'Sampler provides a substantive written response within 10 working days of acknowledgement. Complex cases may take up to 20 working days, in which case the candidate will be notified of the delay.'],
                                    ['5. Resolution', "The response will set out Sampler's findings and any action being taken. Where appropriate, this may include a refund, account action against the employer, or a policy change."],
                                    ['6. Escalation', 'If the candidate is not satisfied, they may escalate to Stage 2 (Section 4) within 14 days of receiving the Stage 1 response.'],
                                ]}
                            />

                            <SubHeading>3.2 Employer Client Complaints</SubHeading>
                            <Clause>The following process applies to complaints raised by employer clients:</Clause>
                            <Table
                                headers={['Step', 'Action']}
                                rows={[
                                    ['1. Submit', 'Employer emails complaints@sampler.org.uk or billing@sampler.org.uk (for payment disputes) with a clear description of the issue and relevant booking or invoice reference.'],
                                    ['2. Acknowledge', 'Sampler acknowledges receipt within 2 working days.'],
                                    ['3. Investigate', 'Sampler reviews the relevant records and, where the complaint involves a candidate, may contact the candidate for their account of events.'],
                                    ['4. Respond', 'Substantive response within 10 working days. Payment disputes will be reviewed against the Payment Terms and Refund Policy.'],
                                    ['5. Resolution', 'Where a complaint is upheld, Sampler will take appropriate action, which may include a credit, refund, or process improvement.'],
                                    ['6. Escalation', 'If unsatisfied, the employer may escalate to Stage 2 within 14 days.'],
                                ]}
                            />
                        </Section>

                        {/* 4 */}
                        <Section title="4. Stage 2 — Escalation">
                            <Clause>If a complainant is not satisfied with the Stage 1 response, they may request a Stage 2 review by emailing complaints@sampler.org.uk with the subject line "Stage 2 escalation — [reference number]".</Clause>
                            <Clause>At Stage 2:</Clause>
                            <BulletList items={[
                                'The complaint will be reviewed by Jack (Co-Founder)',
                                'A final written response will be issued within 15 working days',
                                "The Stage 2 response represents Sampler's final position",
                            ]} />
                            <Clause>If the complainant remains unsatisfied following Stage 2, they may pursue external resolution as set out in Section 7.</Clause>
                        </Section>

                        {/* 5 */}
                        <Section title="5. Dispute Categories and Specific Handling">
                            <SubHeading>5.1 Payment and Refund Disputes</SubHeading>
                            <Clause>Payment disputes are handled in accordance with the Sampler Payment Terms and Refund Policy. In summary:</Clause>
                            <BulletList items={[
                                'Disputes must be raised within 14 days of the invoice or charge date',
                                'Sampler will review whether the charge was correctly applied under the Payment Terms',
                                'Raising a dispute does not suspend the obligation to pay undisputed amounts',
                                'Approved refunds are processed within 10 working days via Stripe to the original payment method',
                            ]} />

                            <SubHeading>5.2 Talent Card Disputes</SubHeading>
                            <Clause>If a candidate believes their Talent Card inaccurately represents their performance or contains a material error, they may raise a complaint setting out:</Clause>
                            <BulletList items={[
                                'The specific signal or output they dispute',
                                'The basis on which they believe it is inaccurate',
                            ]} />
                            <Clause>Sampler will review the assessment responses and AI-generated output against the signal framework. Where a material error is confirmed, Sampler will correct the Talent Card. Sampler does not guarantee any particular Talent Card outcome, and differences of opinion about scoring are not grounds for amendment. Talent Card outputs are advisory signals, not objective assessments.</Clause>

                            <SubHeading>5.3 Trial Shift Conduct Concerns</SubHeading>
                            <Clause>Where a candidate raises a concern about how they were treated by an employer during a trial shift, Sampler will:</Clause>
                            <BulletList items={[
                                'Log the concern and contact the employer for their account of events',
                                'Review any available platform communications',
                                'Where the concern involves potential discrimination, handle in accordance with the Anti-Discrimination Policy',
                                'Where the concern involves a health and safety incident, request full details and escalate to the relevant authorities if appropriate',
                                "Where an employer is found to have treated a candidate unlawfully or in breach of the Employer Terms, Sampler will suspend or terminate the employer's account.",
                            ]} />
                            <Clause>Sampler is not a party to the employment relationship and cannot adjudicate on employment law claims — candidates with such claims should seek independent legal advice.</Clause>

                            <SubHeading>5.4 Data Protection Complaints</SubHeading>
                            <Clause>
                                Complaints relating to the handling of personal data should be submitted to <a href="mailto:privacy@sampler.org.uk" className="text-[#8B5CF6] hover:underline">privacy@sampler.org.uk</a> and will be handled in accordance with UK GDPR. Sampler will respond within the statutory timeframe of one calendar month. If a complainant is not satisfied with Sampler's response, they have the right to lodge a complaint with the Information Commissioner's Office (ICO) at <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-[#8B5CF6] hover:underline">ico.org.uk</a>.
                            </Clause>
                        </Section>

                        {/* 6 */}
                        <Section title="6. Record Keeping">
                            <Clause>All complaints and disputes are logged with the following information:</Clause>
                            <BulletList items={[
                                'Date received',
                                'Complainant type (candidate / employer / other)',
                                'Category of complaint',
                                'Brief description',
                                'Date acknowledged',
                                'Date resolved',
                                'Outcome',
                            ]} />
                            <Clause>Complaint records are retained for 3 years from the date of resolution. This log is reviewed quarterly by the founding team to identify any recurring issues requiring product or policy changes.</Clause>
                        </Section>

                        {/* 7 */}
                        <Section title="7. External Resolution">
                            <Clause>If a complaint cannot be resolved through Sampler's internal procedure, the following external routes are available depending on the nature of the complaint:</Clause>
                            <BulletList items={[
                                <>General disputes: the courts of England and Wales, subject to the governing law provisions in the applicable Terms</>,
                                <>Employment law matters: ACAS early conciliation (acas.org.uk) and the Employment Tribunal</>,
                                <>Data protection: the Information Commissioner's Office (ico.org.uk)</>,
                                <>Equality and discrimination: the Equality and Human Rights Commission (equalityhumanrights.com)</>,
                                <>Consumer matters: the relevant trading standards authority or small claims court</>,
                            ]} />
                            <Clause>Sampler is willing to participate in mediation as an alternative to litigation in appropriate cases. Parties wishing to propose mediation should contact complaints@sampler.org.uk.</Clause>
                        </Section>

                        {/* 8 */}
                        <Section title="8. Policy Review">
                            <Clause>This procedure will be reviewed annually and updated to reflect changes in the platform, applicable law, or complaint patterns identified in the quarterly log review.</Clause>
                            <Clause className="font-semibold text-slate-700">Version 1.1 | Next review: July 2027 | Owner: Jack (Co-Founder, Sampler)</Clause>
                        </Section>

                    </div>
                </div>
            </div>
        </div>
    );
};

/* ── Shared sub-components ── */

const Section = ({ title, children }) => (
    <section>
        <h2 className="text-sm font-bold mb-4 text-[#0A0A0A] uppercase tracking-widest" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            {title}
        </h2>
        <div className="space-y-3 text-sm leading-relaxed">{children}</div>
    </section>
);

const SubHeading = ({ children }) => (
    <h3 className="font-semibold text-slate-800 mt-4 mb-1" style={{ fontFamily: 'Barlow, sans-serif' }}>{children}</h3>
);

const Clause = ({ children }) => (
    <p>{children}</p>
);

const BulletList = ({ items }) => (
    <ul className="list-disc ml-5 space-y-1.5">
        {items.map((item, i) => <li key={i}>{item}</li>)}
    </ul>
);

const Table = ({ headers, rows }) => (
    <div className="mt-4 mb-2 overflow-x-auto">
        <table className="w-full text-sm border-collapse">
            <thead>
                <tr className="bg-purple-50">
                    {headers.map((h, i) => (
                        <th
                            key={i}
                            className={`text-left px-4 py-3 font-semibold text-slate-700 border border-purple-100 ${i === 0 ? 'rounded-tl-lg w-1/5' : ''} ${i === headers.length - 1 ? 'rounded-tr-lg' : ''}`}
                        >
                            {h}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                        {row.map((cell, j) => (
                            <td key={j} className="px-4 py-3 border border-purple-100 text-slate-600 align-top">{cell}</td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

export default ComplaintsDisputes;
